/**
 * React Query hooks for DS Coder user-authored rule management (scope-aware CRUD).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createRule,
  deleteRule,
  listRules,
  updateRule,
} from "@/services/dsCoderRulesService";
import { clearDsCoderRulesCache } from "@/services/dsCoder/rulesRepository";
import type { DsCoderRuleInput, RuleScope } from "@/types/dsCoderRules";

function rulesKey(scope: RuleScope, workspaceId?: string | null) {
  return ["ds-coder-rules", scope, scope === "workspace" ? workspaceId ?? null : scope] as const;
}

export function useDsCoderRules(scope: RuleScope, workspaceId?: string | null) {
  return useQuery({
    queryKey: rulesKey(scope, workspaceId),
    queryFn: () => listRules({ scope, workspaceId }),
    enabled: scope !== "workspace" || Boolean(workspaceId),
  });
}

export function useDsCoderRuleMutations(scope: RuleScope, workspaceId?: string | null) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: rulesKey(scope, workspaceId) });
    // Chat-time resolution caches merged rules; drop it so edits apply immediately.
    clearDsCoderRulesCache();
  };

  const create = useMutation({
    mutationFn: (input: DsCoderRuleInput) => createRule({ scope, workspaceId, input }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<DsCoderRuleInput> }) =>
      updateRule(id, patch),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteRule(id),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}
